/**
 * EnemyRenderer - Renders enemies in the 3D scene
 */
import * as THREE from 'three';
import * as SkeletonUtils from 'three/examples/jsm/utils/SkeletonUtils.js';
import EnemyModelLoader from './EnemyModelLoader.js';

export class EnemyRenderer {
  /**
   * Create a new EnemyRenderer
   * @param {Object} options - Options for initialization
   * @param {THREE.Scene} options.scene - The Three.js scene for rendering
   * @param {Object} options.assetManager - The asset manager for loading models
   */
  constructor(options = {}) {
    console.log('Initializing EnemyRenderer with options:', {
      hasScene: !!options.scene,
      hasAssetManager: !!options.assetManager
    });
    
    const scene = options.scene;
    this.assetManager = options.assetManager;
    
    if (!scene) {
      console.error('Invalid scene passed to EnemyRenderer');
      this.scene = null;
    } else if (typeof scene.add === 'function') {
      this.scene = scene; // Direct Three.js Scene
    } else if (scene.scene && typeof scene.scene.add === 'function') {
      this.scene = scene.scene; // Object containing a scene
    } else {
      console.error('Invalid scene: missing add method');
      this.scene = null;
    }
    
    this.enemyObjects = {}; // Map of enemy ID to { mesh, healthBar, mixer, animations }
    this.enemyModelLoader = new EnemyModelLoader();
    
    // Initialize model loader
    this.modelLoadingErrors = new Set(); // Track which models failed to load
    this.initializeModelLoader();
    
    console.log('EnemyRenderer initialized with scene:', !!this.scene);
  }

  /**
   * Initialize the model loader and preload common enemy models
   */
  async initializeModelLoader() {
    const commonModels = ['Skeleton_Warrior', 'Skeleton_Rogue', 'Skeleton_Mage', 'Skeleton_Minion'];
    
    for (const modelType of commonModels) {
      try {
        await this.enemyModelLoader.loadEnemyModel(modelType);
        console.log(`Successfully preloaded ${modelType} model`);
      } catch (error) {
        console.error(`Failed to preload ${modelType} model:`, error);
        this.modelLoadingErrors.add(modelType);
      }
    }
  }

  /**
   * Add a new enemy visual representation
   * @param {Object} enemy - Enemy object with id, type, and position
   */
  async addEnemy(enemy) {
    if (!this.scene) {
      console.error('Cannot add enemy: scene not available');
      return;
    }
    
    if (!enemy || !enemy.id) {
      console.error('Cannot add enemy: invalid enemy object', enemy);
      return;
    }
    
    try {
      // Check if this enemy already has a mesh in the scene
      if (this.enemyObjects[enemy.id]) {
        console.warn(`Enemy ${enemy.id} already has a visual representation`);
        return;
      }
      
      const id = enemy.id;
      const position = enemy.position || { x: 0, y: 0, z: 0 };
      
      // Determine the actual skeleton model type based on enemy type
      let modelType;
      switch (enemy.type) {
        case 'melee':
          modelType = 'Skeleton_Warrior';
          break;
        case 'warrior':
          modelType = 'Skeleton_Warrior';
          break;
        case 'ranged':
          modelType = 'Skeleton_Rogue';
          break;
        case 'mage':
          modelType = 'Skeleton_Mage';
          break;
        case 'boss':
          modelType = 'Skeleton_Mage'; // Boss is a powerful mage for now
          break;
        default:
          modelType = 'Skeleton_Minion';
      }
      
      // If the modelType was specified directly in the enemy object, use that instead
      if (enemy.modelType && typeof enemy.modelType === 'string') {
        console.log(`Using explicitly specified model type: ${enemy.modelType} for enemy ${id}`);
        modelType = enemy.modelType;
      }
      
      // Check if this model type has previously failed to load
      if (this.modelLoadingErrors.has(modelType)) {
        console.warn(`Model ${modelType} previously failed to load, using fallback immediately`);
        this._createFallbackEnemy(enemy);
        return;
      }
      
      // Check if this is a boss enemy
      const isBoss = enemy.isBoss === true;
      const scale = enemy.scale || (isBoss ? 3.0 : 1.0);
      
      console.log(`Adding enemy ${id} (${modelType})${isBoss ? ' [BOSS]' : ''} to scene at position:`, position);
      if (isBoss || scale !== 1.0) {
        console.log(`Enemy is a boss or has custom scale: ${scale}x`);
      }
      
      // Load the model with animations
      try {
        const { model, animations } = await this.enemyModelLoader.loadEnemyModel(modelType);
        
        if (!model) {
          throw new Error(`Failed to load model for ${modelType}`);
        }
        
        // Clone the model for this instance
        const enemyModel = SkeletonUtils.clone(model);
        
        // Position the model
        enemyModel.position.set(position.x, position.y || 0, position.z);
        
        // Apply scale if it's a boss or has custom scaling
        if (scale !== 1.0) {
          enemyModel.scale.set(scale, scale, scale);
        }
        
        // Add to scene
        this.scene.add(enemyModel);
        
        // Set up animation mixer
        const mixer = new THREE.AnimationMixer(enemyModel);
        
        // Get walk animation and play it by default
        let walkAnimation = this.enemyModelLoader.getAnimationByName({ animations }, 'walk');
        if (!walkAnimation) {
          // Try alternate animation names
          walkAnimation = this.enemyModelLoader.getAnimationByName({ animations }, 'walking');
          if (!walkAnimation) {
            walkAnimation = this.enemyModelLoader.getAnimationByName({ animations }, 'move');
          }
        }
        
        let walkAction = null;
        if (walkAnimation) {
          walkAction = mixer.clipAction(walkAnimation);
          walkAction.play();
        } else {
          console.warn(`No walk animation found for ${modelType}`);
        }
        
        // Find attack animation but don't play it yet
        let attackAnimation = this.enemyModelLoader.getAnimationByName({ animations }, 'attack');
        if (!attackAnimation) {
          // Try alternate animation names
          attackAnimation = this.enemyModelLoader.getAnimationByName({ animations }, 'attacking');
          if (!attackAnimation) {
            attackAnimation = this.enemyModelLoader.getAnimationByName({ animations }, 'melee');
          }
        }
        
        let attackAction = null;
        if (attackAnimation) {
          attackAction = mixer.clipAction(attackAnimation);
          // Don't play attack yet, just prepare it
          attackAction.loop = THREE.LoopOnce;
          attackAction.clampWhenFinished = true;
        } else {
          console.warn(`No attack animation found for ${modelType}`);
        }
        
        // Add collision detection properties
        enemyModel.userData = {
          type: 'enemy',
          enemyType: modelType,
          id: id,
          health: enemy.health,
          maxHealth: enemy.maxHealth
        };
        
        // Add a health bar (red sprite above enemy)
        const healthBarWidth = 1.0;
        const healthBarHeight = 0.15;
        
        // Determine appropriate height for the health bar based on model type
        let healthBarY = 2.0; // Default height
        
        // Create a container for the health bar system
        const healthBarContainer = new THREE.Object3D();
        healthBarContainer.position.set(0, healthBarY, 0); // Above the enemy
        enemyModel.add(healthBarContainer);
        
        // Create background for the health bar (gray)
        const healthBarBgMaterial = new THREE.SpriteMaterial({ 
          color: 0x444444,
          depthTest: false,
          sizeAttenuation: true
        });
        const healthBarBg = new THREE.Sprite(healthBarBgMaterial);
        healthBarBg.scale.set(healthBarWidth, healthBarHeight, 1);
        healthBarContainer.add(healthBarBg);
        
        // Create the actual health bar (red)
        const healthBarMaterial = new THREE.SpriteMaterial({ 
          color: 0xff0000,
          depthTest: false,
          sizeAttenuation: true
        });
        const healthBar = new THREE.Sprite(healthBarMaterial);
        healthBar.scale.set(healthBarWidth, healthBarHeight, 1);
        healthBar.position.set(0, 0, 0.01); // Slightly in front of the background
        healthBarContainer.add(healthBar);
        
        // Store the mesh with the enemy object
        this.enemyObjects[id] = { 
          mesh: enemyModel, 
          healthBar,
          healthBarBg,
          healthBarContainer,
          healthBarWidth,
          mixer,
          animations: {
            walk: walkAction,
            attack: attackAction
          },
          currentAnimation: 'walk',
          enemy: enemy, // Keep a reference to the enemy object
          modelType
        };
        
        // Give the enemy a reference to its mesh
        enemy.mesh = enemyModel;
        
        console.log(`Enemy ${id} added to scene with ${modelType} model`);
      } catch (modelError) {
        console.error(`Error loading model for enemy ${id}:`, modelError);
        // Add to failed models set
        this.modelLoadingErrors.add(modelType);
        // Fallback to simple sphere if model loading fails
        this._createFallbackEnemy(enemy);
      }
    } catch (error) {
      console.error(`Error adding enemy:`, error);
      // Fallback to simple sphere
      this._createFallbackEnemy(enemy);
    }
  }

  /**
   * Create a fallback enemy representation if model loading fails
   * @private
   * @param {Object} enemy - Enemy object
   */
  _createFallbackEnemy(enemy) {
    if (!this.scene || !enemy || !enemy.id) return;
    
    try {
      const id = enemy.id;
      const type = enemy.type || 'default';
      const position = enemy.position || { x: 0, y: 0, z: 0 };
      
      console.log(`Creating fallback representation for enemy ${id} (${type})`);
      
      // Get color based on enemy type
      let color;
      switch (type) {
        case 'melee':
          color = 0xff0000; // Red for melee enemies
          break;
        case 'ranged':
          color = 0x00ff00; // Green for ranged enemies
          break;
        case 'mage':
          color = 0x0000ff; // Blue for mage enemies
          break;
        case 'boss':
          color = 0xff6600; // Orange for boss enemies
          break;
        default:
          color = 0x8844ff; // Purple for default enemies
      }
      
      // Create a sphere as fallback
      const geometry = new THREE.SphereGeometry(0.5, 16, 16);
      const material = new THREE.MeshStandardMaterial({
        color: color,
        roughness: 0.6,
        metalness: 0.4,
        emissive: color,
        emissiveIntensity: 0.2 // Slight glow effect
      });
      const mesh = new THREE.Mesh(geometry, material);
      
      // Position the mesh
      mesh.position.set(position.x, position.y || 0.5, position.z);
      
      // Add collision detection properties
      mesh.userData = {
        type: 'enemy',
        id: id,
        health: enemy.health,
        maxHealth: enemy.maxHealth
      };
      
      // Add to scene
      this.scene.add(mesh);

      // Add a health bar (red sprite above enemy)
      const healthBarWidth = 1.0;
      const healthBarHeight = 0.15;
      
      // Create a container for the health bar system
      const healthBarContainer = new THREE.Object3D();
      healthBarContainer.position.set(0, 0.8, 0); // Above the enemy
      mesh.add(healthBarContainer);
      
      // Create background for the health bar (gray)
      const healthBarBgMaterial = new THREE.SpriteMaterial({ 
        color: 0x444444,
        depthTest: false,
        sizeAttenuation: true
      });
      const healthBarBg = new THREE.Sprite(healthBarBgMaterial);
      healthBarBg.scale.set(healthBarWidth, healthBarHeight, 1);
      healthBarContainer.add(healthBarBg);
      
      // Create the actual health bar (red)
      const healthBarMaterial = new THREE.SpriteMaterial({ 
        color: 0xff0000,
        depthTest: false,
        sizeAttenuation: true
      });
      const healthBar = new THREE.Sprite(healthBarMaterial);
      healthBar.scale.set(healthBarWidth, healthBarHeight, 1);
      healthBar.position.set(0, 0, 0.01); // Slightly in front of the background
      healthBarContainer.add(healthBar);
      
      // Apply a consistent scale factor that will be adjusted by distance
      this._updateHealthBarScale(healthBarContainer);

      // Store the mesh with the enemy object
      this.enemyObjects[id] = { 
        mesh, 
        healthBar,
        healthBarBg,
        healthBarContainer,
        healthBarWidth,
        enemy: enemy, // Keep a reference to the enemy object
        isFallback: true
      };
      
      // Give the enemy a reference to its mesh
      enemy.mesh = mesh;
      
      console.log(`Fallback enemy ${id} added to scene`);
    } catch (error) {
      console.error(`Error creating fallback enemy:`, error);
    }
  }

  /**
   * Play animation on enemy
   * @param {string} id - Enemy ID
   * @param {string} animationName - Name of animation to play ('walk' or 'attack')
   */
  playAnimation(id, animationName) {
    const obj = this.enemyObjects[id];
    if (!obj || obj.isFallback || !obj.mixer || !obj.animations) {
      return; // Can't play animations on fallback or without mixer
    }
    
    // Don't restart the same animation
    if (obj.currentAnimation === animationName) {
      return;
    }
    
    // CRITICAL FIX: Handle case where animation doesn't exist
    let action = obj.animations[animationName];
    
    // If idle animation is requested but doesn't exist, use walk animation as fallback
    if (!action && animationName === 'idle' && obj.animations.walk) {
      console.log(`Using walk animation instead of idle for enemy ${id}`);
      action = obj.animations.walk;
      // Keep track of the real animation being used
      animationName = 'walk';
    }
    
    // If requested animation doesn't exist, warn and return
    if (!action) {
      console.warn(`Animation ${animationName} not found for enemy ${id}`);
      return;
    }
    
    // Stop current animation with crossfade
    if (obj.currentAnimation && obj.animations[obj.currentAnimation]) {
      const currentAction = obj.animations[obj.currentAnimation];
      currentAction.fadeOut(0.3); // Short fade out
    }
    
    // Play new animation with crossfade
    action.reset();
    action.fadeIn(0.3); // Short fade in
    action.play();
    
    obj.currentAnimation = animationName;
    
    // If this is an attack animation (one-shot), queue up transition back to walk
    if (animationName === 'attack' && obj.animations.attack) {
      // Listen for animation completion
      const onFinished = () => {
        // Return to walk animation
        if (obj.animations.walk) {
          this.playAnimation(id, 'walk');
        }
        // Remove listener
        obj.mixer.removeEventListener('finished', onFinished);
      };
      
      obj.mixer.addEventListener('finished', onFinished);
    }
  }

  /**
   * Update an enemy's properties and appearance
   * @param {Object|string} enemyOrId - Enemy object or id
   * @param {Object} position - Updated position coordinates
   * @param {number} health - Current health value
   * @param {number} maxHealth - Maximum health value
   * @param {string} animationState - Animation state to play (idle, walking, attack)
   * @param {Object} extras - Additional properties to update
   */
  updateEnemy(enemyOrId, position, health, maxHealth, animationState, extras = {}) {
    // Extract enemy id
    const id = typeof enemyOrId === 'string' ? enemyOrId : (enemyOrId && enemyOrId.id);
    
    if (!id) {
      console.error('Cannot update enemy: Invalid ID');
      return;
    }
    
    // Get the enemy object
    const enemyObject = this.enemyObjects[id];
    
    if (!enemyObject) {
      //console.warn(`Cannot update enemy ${id}: Not found in renderer`);
      return;
    }
    
    // Update position if provided
    if (position && typeof position === 'object') {
      if (enemyObject.mesh) {
        enemyObject.mesh.position.x = position.x;
        // Only update y if it's provided and non-zero (maintain current height otherwise)
        if (typeof position.y === 'number' && position.y !== 0) {
          enemyObject.mesh.position.y = position.y;
        }
        enemyObject.mesh.position.z = position.z;
      }
    }
    
    // Update health if provided
    if (typeof health === 'number' && typeof maxHealth === 'number') {
      // Update stored values
      if (enemyObject.mesh && enemyObject.mesh.userData) {
        enemyObject.mesh.userData.health = health;
        enemyObject.mesh.userData.maxHealth = maxHealth;
      }
      
      // Update health bar
      if (enemyObject.healthBar) {
        const healthPercent = Math.max(0, Math.min(1, health / maxHealth));
        enemyObject.healthBar.scale.x = healthPercent * 1.0; // 1.0 is full width
      }
    }
    
    // Update scale if provided in extras
    if (extras && typeof extras.scale === 'number' && extras.scale > 0) {
      const scale = extras.scale;
      if (enemyObject.mesh && (enemyObject.currentScale !== scale)) {
        enemyObject.mesh.scale.set(scale, scale, scale);
        enemyObject.currentScale = scale;
        console.log(`Updated enemy ${id} scale to ${scale}x`);
      }
    }
    
    // Update boss status if provided
    if (extras && typeof extras.isBoss === 'boolean') {
      const isBoss = extras.isBoss;
      if (enemyObject.mesh && (enemyObject.isBoss !== isBoss)) {
        enemyObject.isBoss = isBoss;
        
        // If it's a boss and scale wasn't specified, apply default boss scale
        if (isBoss && !extras.scale && (!enemyObject.currentScale || enemyObject.currentScale === 1.0)) {
          const bossScale = 3.0;
          enemyObject.mesh.scale.set(bossScale, bossScale, bossScale);
          enemyObject.currentScale = bossScale;
          console.log(`Applied default boss scale (${bossScale}x) to enemy ${id}`);
        }
        
        console.log(`Updated enemy ${id} boss status to ${isBoss}`);
      }
    }
    
    // Play animation if specified
    if (animationState && animationState !== enemyObject.currentAnimation) {
      this.playAnimation(id, animationState);
    }
  }

  /**
   * Remove an enemy's visual representation
   * @param {Object|string} enemyOrId - Enemy object or ID to remove
   */
  removeEnemy(enemyOrId) {
    // CRITICAL FIX: Handle both string IDs and objects with id property
    let id;
    
    if (typeof enemyOrId === 'string') {
      // Direct string ID
      id = enemyOrId;
    } else if (enemyOrId && typeof enemyOrId === 'object') {
      // Object with ID property
      id = enemyOrId.id;
    
      // If we couldn't get an ID from the object
    if (!id) {
        console.error('Cannot remove enemy: invalid object without ID property', enemyOrId);
        return;
      }
    } else {
      console.error('Cannot remove enemy: invalid ID', enemyOrId);
      return;
    }
    
    try {
      const obj = this.enemyObjects[id];
      if (!obj) {
        console.warn(`Cannot remove enemy ${id}: not found in renderer`);
        return;
      }
      
      console.log(`Removing enemy ${id} from scene`);
      
      // Clean up animations if this is a model with animations
      if (obj.mixer) {
        // Stop all animations
        if (obj.animations) {
          Object.values(obj.animations).forEach(action => {
            if (action) action.stop();
          });
        }
        // Uncache mixer
        obj.mixer.stopAllAction();
        obj.mixer.uncacheRoot(obj.mesh);
      }
      
      // Remove from scene and dispose geometries/materials
      if (this.scene && obj.mesh) {
        this.scene.remove(obj.mesh);
      }
      
      // Dispose of resources
      if (obj.mesh) {
        // For regular mesh
        if (obj.mesh.geometry) obj.mesh.geometry.dispose();
        
        // For materials
        if (obj.mesh.material) {
          if (Array.isArray(obj.mesh.material)) {
            obj.mesh.material.forEach(m => m.dispose());
          } else {
            obj.mesh.material.dispose();
          }
        }
        
        // For complex model with children
        obj.mesh.traverse(child => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
      }
      
      // Dispose health bar components
      if (obj.healthBar && obj.healthBar.material) {
        obj.healthBar.material.dispose();
      }
      
      if (obj.healthBarBg && obj.healthBarBg.material) {
        obj.healthBarBg.material.dispose();
      }
      
      // Remove mesh reference from enemy object
      if (obj.enemy && obj.enemy.mesh) {
        obj.enemy.mesh = null;
      }
      
      // Remove reference
      delete this.enemyObjects[id];
      
      console.log(`Successfully removed enemy ${id} from renderer`);
    } catch (error) {
      console.error(`Error removing enemy ${id}:`, error);
    }
  }

  /**
   * Get an enemy object by ID
   * @param {string} id - Enemy ID
   * @returns {Object|null} - Enemy object or null if not found
   */
  getEnemyObject(id) {
    if (!this.enemyObjects || !id) return null;
    return this.enemyObjects[id] || null;
  }

  /**
   * Update method to be called in the animation loop
   * @param {number} deltaTime - Time in seconds since last update
   */
  update(deltaTime) {
    // Update animation mixers
    for (const id in this.enemyObjects) {
      const obj = this.enemyObjects[id];
      
      // Update mixer if available (for animated models)
      if (obj.mixer) {
        obj.mixer.update(deltaTime);
      }
    }
  }

  /**
   * Dispose of all resources
   */
  dispose() {
    // Clean up all enemy objects
    for (const id in this.enemyObjects) {
      this.removeEnemy(id);
    }
    this.enemyObjects = {};
  }

  /**
   * Utility to scale health bar appropriately
   * @private
   * @param {THREE.Object3D} container - Health bar container
   */
  _updateHealthBarScale(container) {
    if (!container) return;
    
    const scale = 0.5; // Base scale for health bar
    container.scale.set(scale, scale, scale);
  }

  /**
   * Update health bars for all enemies to maintain consistent size
   * @param {THREE.Camera} camera - The camera to calculate distance from
   */
  updateHealthBars(camera) {
    if (!camera) return;
    
    for (const id in this.enemyObjects) {
      const obj = this.enemyObjects[id];
      if (obj && obj.healthBarContainer) {
        // Get distance to camera
        const distance = camera.position.distanceTo(obj.mesh.position);
        
        // Calculate scale based on distance
        // We want the health bar to maintain a consistent screen size
        // Larger values make the health bar bigger
        const scale = Math.min(0.7, Math.max(0.4, distance * 0.05));
        
        // Apply scale to container
        obj.healthBarContainer.scale.set(scale, scale, scale);
        
        // Hide health bars that are too far away (optional)
        const maxVisibleDistance = 30;
        obj.healthBarContainer.visible = distance < maxVisibleDistance;
      }
    }
  }

  /**
   * Create a 3D enemy representation
   * @param {Object} enemy - Enemy data
   * @returns {Object} - The created THREE.js mesh
   */
  createEnemyMesh(enemy) {
    try {
      // Check if we have a valid model type from server
      const modelType = enemy.modelType || 'Skeleton_Minion';
      
      // Attempt to get model from EnemyModelLoader
      if (window.enemyModelLoader) {
        // Get the model from the loader (with caching)
        const model = window.enemyModelLoader.getModel(modelType);
        
        if (model) {
          console.log(`Creating ${modelType} enemy with model`);
          
          // Clone the model to create a new instance
          const clonedModel = model.clone();
          
          // Apply boss scaling if needed
          if (enemy.isBoss || enemy.scale) {
            const scale = enemy.scale || 2.0; // Default boss scale is 2x
            clonedModel.scale.set(scale, scale, scale);
          }
          
          // Set initial position
          if (enemy.position) {
            clonedModel.position.set(
              enemy.position.x || 0,
              enemy.position.y || 0,
              enemy.position.z || 0
            );
          }
          
          // Return the model
          return clonedModel;
        }
      }
      
      // Fallback: Create sphere mesh
      const meshGeometry = new THREE.SphereGeometry(1, 8, 8);
      const meshMaterial = new THREE.MeshBasicMaterial({ 
        color: this._getEnemyColor(enemy.type),
        transparent: true,
        opacity: 0.8,
        emissive: this._getEnemyColor(enemy.type),
        emissiveIntensity: 0.5
      });
      
      const mesh = new THREE.Mesh(meshGeometry, meshMaterial);
      
      // Apply boss scaling if needed
      if (enemy.isBoss || enemy.scale) {
        const scale = enemy.scale || 2.0; // Default boss scale is 2x
        mesh.scale.set(scale, scale, scale);
      }
      
      return mesh;
    } catch (error) {
      console.error('Error creating enemy mesh:', error);
      
      // Create a basic fallback mesh
      const fallbackMesh = new THREE.Mesh(
        new THREE.BoxGeometry(1, 1, 1),
        new THREE.MeshBasicMaterial({ color: 0xff0000 })
      );
      
      return fallbackMesh;
    }
  }
} 